import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Search, Trash2, Download, MoreHorizontal, FileText, Image as ImageIcon, Video, File, Loader2, Users } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getFileIcon = (type) => {
  const icons = { image: ImageIcon, video: Video, pdf: FileText, document: FileText };
  return icons[type] || File;
};

const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const MediaList = () => {
  const [media, setMedia] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadData, setUploadData] = useState({ title: '', description: '', requires_lead_capture: true, category: 'general', tags: '' });
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [mediaRes, leadsRes] = await Promise.all([
        axios.get(`${API}/admin/media?include_inactive=true`, { withCredentials: true }),
        axios.get(`${API}/admin/media/leads/list`, { withCredentials: true })
      ]);
      setMedia(mediaRes.data);
      setLeads(leadsRes.data);
    } catch (error) { toast.error('Failed to fetch'); }
    finally { setLoading(false); }
  };

  const handleDelete = async () => {
    try {
      console.log('Attempting to delete media:', deleteId);
      const response = await axios.delete(`${API}/admin/media/${deleteId}`, { withCredentials: true });
      console.log('Delete response:', response.data);
      toast.success('Deleted');
      fetchData();
    } catch (error) {
      console.error('Delete error:', error.response?.data || error.message);
      toast.error(error.response?.data?.detail || 'Failed');
    } finally {
      setDeleteId(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !uploadData.title) { toast.error('Select file and enter title'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', selectedFile);
      fd.append('title', uploadData.title);
      fd.append('description', uploadData.description);
      fd.append('requires_lead_capture', uploadData.requires_lead_capture);
      fd.append('category', uploadData.category);
      fd.append('tags', uploadData.tags);
      await axios.post(`${API}/admin/media`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Uploaded');
      setShowUpload(false);
      setUploadData({ title: '', description: '', requires_lead_capture: true, category: 'general', tags: '' });
      setSelectedFile(null);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
    finally { setUploading(false); }
  };

  const filtered = media.filter(m => m.title?.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="text-2xl font-bold">Media Resources</h1><p className="text-gray-500">Manage downloadable resources</p></div>
        <Button onClick={() => setShowUpload(true)} className="bg-[#C8473A] hover:bg-[#B74F40]"><Plus className="w-4 h-4 mr-2" /> Upload</Button>
      </div>

      <Tabs defaultValue="media">
        <TabsList><TabsTrigger value="media">Resources ({media.length})</TabsTrigger><TabsTrigger value="leads">Leads ({leads.length})</TabsTrigger></TabsList>
        
        <TabsContent value="media">
          <div className="mb-6"><div className="relative max-w-md"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" /><Input placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" /></div></div>
          <div className="bg-white rounded-lg border">
            <Table>
              <TableHeader><TableRow><TableHead>Resource</TableHead><TableHead>Type</TableHead><TableHead>Size</TableHead><TableHead>Downloads</TableHead><TableHead>Gated</TableHead><TableHead className="w-12"></TableHead></TableRow></TableHeader>
              <TableBody>
                {loading ? <TableRow><TableCell colSpan={6} className="text-center py-8">Loading...</TableCell></TableRow> :
                filtered.map(item => {
                  const FileIcon = getFileIcon(item.file_type);
                  return (
                    <TableRow key={item.media_id}>
                      <TableCell><div className="flex items-center gap-3"><div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center"><FileIcon className="w-5 h-5 text-gray-500" /></div><div><p className="font-medium">{item.title}</p><p className="text-sm text-gray-500">{item.file_name}</p></div></div></TableCell>
                      <TableCell><Badge variant="outline">{item.file_type}</Badge></TableCell>
                      <TableCell>{formatFileSize(item.file_size)}</TableCell>
                      <TableCell><div className="flex items-center gap-1"><Download className="w-4 h-4 text-gray-400" />{item.download_count || 0}</div></TableCell>
                      <TableCell><Badge variant={item.requires_lead_capture ? 'default' : 'secondary'}>{item.requires_lead_capture ? 'Yes' : 'No'}</Badge></TableCell>
                      <TableCell>
                        <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem asChild><a href={`${BACKEND_URL}${item.file_url}`} download><Download className="w-4 h-4 mr-2" /> Download</a></DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setDeleteId(item.media_id)} className="text-red-600"><Trash2 className="w-4 h-4 mr-2" /> Delete</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="leads">
          <div className="bg-white rounded-lg border">
            <Table>
              <TableHeader><TableRow><TableHead>Lead</TableHead><TableHead>Company</TableHead><TableHead>Resource</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
              <TableBody>
                {leads.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center py-8"><Users className="w-12 h-12 mx-auto text-gray-300 mb-2" /><p className="text-gray-500">No leads yet</p></TableCell></TableRow> :
                leads.map(lead => (
                  <TableRow key={lead.lead_id}>
                    <TableCell><div><p className="font-medium">{lead.name}</p><p className="text-sm text-gray-500">{lead.email}</p></div></TableCell>
                    <TableCell>{lead.company || '-'}</TableCell>
                    <TableCell>{media.find(m => m.media_id === lead.resource_id)?.title || lead.resource_id}</TableCell>
                    <TableCell>{new Date(lead.created_at).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Upload Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent>
          <DialogHeader><DialogTitle>Upload Resource</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>File</Label><Input type="file" onChange={(e) => setSelectedFile(e.target.files[0])} /></div>
            <div><Label>Title *</Label><Input value={uploadData.title} onChange={(e) => setUploadData({ ...uploadData, title: e.target.value })} /></div>
            <div><Label>Description</Label><Textarea value={uploadData.description} onChange={(e) => setUploadData({ ...uploadData, description: e.target.value })} /></div>
            <div><Label>Category</Label><Input value={uploadData.category} onChange={(e) => setUploadData({ ...uploadData, category: e.target.value })} /></div>
            <div><Label>Tags (comma-separated)</Label><Input value={uploadData.tags} onChange={(e) => setUploadData({ ...uploadData, tags: e.target.value })} /></div>
            <div className="flex items-center gap-3"><Switch checked={uploadData.requires_lead_capture} onCheckedChange={(v) => setUploadData({ ...uploadData, requires_lead_capture: v })} /><Label>Require email to download</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpload(false)}>Cancel</Button>
            <Button onClick={handleUpload} className="bg-[#C8473A] hover:bg-[#B74F40]" disabled={uploading}>{uploading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Upload</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}><AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete</AlertDialogTitle><AlertDialogDescription>This will also delete the file. Cannot be undone.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">Delete</Button></AlertDialogFooter></AlertDialogContent></AlertDialog>
    </div>
  );
};

export default MediaList;
